/** REST boundary for the ABAC access-management surface. */
import { restGet, restPost } from "./restClient.js";

export function newOperationId() {
    const bytes = new Uint8Array(16);
    globalThis.crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    let binary = "";
    for (const byte of bytes) binary += String.fromCharCode(byte);
    return globalThis.btoa(binary);
}

function pick(obj, snake, camel) {
    if (!obj) return undefined;
    return obj[camel] ?? obj[snake];
}

function count(value) {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
}

function enumTail(value, prefix) {
    if (typeof value !== "string") return "unspecified";
    return value.startsWith(prefix) ? value.slice(prefix.length).toLowerCase() : value.toLowerCase();
}

function groupKey(raw) {
    if (!raw) return null;
    return {
        sourceProvider: pick(raw, "source_provider", "sourceProvider") ?? "",
        integrationId: pick(raw, "integration_id", "integrationId") ?? "",
        sourceId: pick(raw, "source_id", "sourceId") ?? "",
    };
}

export function groupKeyId(group) {
    if (!group) return "";
    return `${group.sourceProvider}\u0000${group.integrationId}\u0000${group.sourceId}`;
}

function grantee(raw, display = "") {
    const viewerId = pick(raw, "viewer_user_id", "viewerUserId");
    if (viewerId) return { type: "viewer", viewerUserId: viewerId, display: display || viewerId };
    const directoryGroup = groupKey(pick(raw, "directory_group", "directoryGroup"));
    if (directoryGroup) return { type: "group", directoryGroup, display: display || directoryGroup.sourceId };
    return { type: "unknown", display: display || "Unknown grantee" };
}

function annotation(raw) {
    return {
        group: groupKey(raw?.group),
        displayName: pick(raw, "display_name", "displayName") || "",
        presence: enumTail(raw?.presence, "GROUP_PRESENCE_"),
        memberCount: count(pick(raw, "member_count", "memberCount")),
    };
}

export function transformGrant(raw) {
    const scopeGroups = pick(raw, "scope_groups", "scopeGroups") ?? [];
    const annotations = pick(raw, "scope_group_annotations", "scopeGroupAnnotations") ?? [];
    return {
        id: raw?.id ?? "",
        grantee: grantee(raw?.grantee, pick(raw, "grantee_display", "granteeDisplay")),
        roleSlug: pick(raw, "role_slug", "roleSlug") ?? "",
        scopeGroups: Array.isArray(scopeGroups) ? scopeGroups.map(groupKey).filter(Boolean) : [],
        scopeAnnotations: Array.isArray(annotations) ? annotations.map(annotation) : [],
        granteePresence: enumTail(pick(raw, "grantee_presence", "granteePresence"), "GROUP_PRESENCE_"),
        effectiveSubjectCount: count(pick(raw, "effective_subject_count", "effectiveSubjectCount")),
        granteeMemberCount: count(pick(raw, "grantee_member_count", "granteeMemberCount")),
        bindingTable: pick(raw, "binding_table", "bindingTable") ?? "GRANT_BINDING_TABLE_UNSPECIFIED",
        impactDegraded: Boolean(pick(raw, "impact_degraded", "impactDegraded")),
    };
}

export async function listGrants({ pageSize = 25, pageToken = "", search = "", bindingTable = "GRANT_BINDING_TABLE_UNSPECIFIED" } = {}) {
    const raw = await restGet("/v1/access/grants", {
        page_size: pageSize,
        page_token: pageToken,
        search,
        binding_table: bindingTable,
    });
    return {
        grants: (raw?.grants ?? []).map(transformGrant),
        nextPageToken: pick(raw, "next_page_token", "nextPageToken") ?? "",
        totalCount: count(pick(raw, "total_count", "totalCount")),
    };
}

/** A server-filtered inventory; never accept an older server's tenant-wide list. */
export async function listViewerGrants({ viewerUserId, pageSize = 25, pageToken = "" }) {
    const raw = await restGet("/v1/access/grants", {
        viewer_user_id: viewerUserId,
        page_size: pageSize,
        page_token: pageToken,
    });
    if (pick(raw, "resolved_viewer_user_id", "resolvedViewerUserId") !== viewerUserId) {
        throw new Error("Configured grant details are not available on this server yet.");
    }
    const inheritanceStatus = enumTail(pick(raw, "inheritance_status", "inheritanceStatus"), "GRANT_INHERITANCE_STATUS_");
    if (!["resolved", "viewer_unmapped", "no_active_generation"].includes(inheritanceStatus)) {
        throw new Error("Group inheritance could not be determined. Refresh to try again.");
    }
    return {
        grants: (raw?.grants ?? []).map(transformGrant),
        nextPageToken: pick(raw, "next_page_token", "nextPageToken") ?? "",
        totalCount: count(pick(raw, "total_count", "totalCount")),
        inheritanceStatus,
    };
}

function wireGroup(group) {
    return {
        source_provider: group.sourceProvider,
        integration_id: group.integrationId,
        source_id: group.sourceId,
    };
}

function wireGrantee(value) {
    if (value.type === "viewer") return { viewer_user_id: value.viewerUserId };
    return { directory_group: wireGroup(value.directoryGroup) };
}

export async function createGrant({ grantee: value, roleId, scopeGroups, operationId }) {
    const raw = await restPost("/v1/access/grants", {
        grantee: wireGrantee(value),
        role_id: roleId,
        scope_groups: scopeGroups.map(wireGroup),
        operation_id: operationId,
    });
    return transformGrant(raw?.grant);
}

export async function updateGrantScope({ bindingTable, bindingId, scopeGroups, operationId }) {
    const raw = await restPost("/v1/access/grants:update-scope", {
        binding_table: bindingTable,
        binding_id: bindingId,
        scope_groups: scopeGroups.map(wireGroup),
        operation_id: operationId,
    });
    return transformGrant(raw?.grant);
}

export function revokeGrant({ bindingTable, bindingId, operationId }) {
    return restPost("/v1/access/grants:revoke", {
        binding_table: bindingTable,
        binding_id: bindingId,
        operation_id: operationId,
    });
}

export async function listGrantRoles({ pageSize = 50, pageToken = "", search = "" } = {}) {
    const raw = await restGet("/v1/access/roles", { page_size: pageSize, page_token: pageToken, search });
    return {
        roles: (raw?.roles ?? []).map((role) => ({
            roleId: pick(role, "role_id", "roleId") ?? "",
            roleSlug: pick(role, "role_slug", "roleSlug") ?? "",
            description: role?.description ?? "",
        })),
        nextPageToken: pick(raw, "next_page_token", "nextPageToken") ?? "",
        totalCount: count(pick(raw, "total_count", "totalCount")),
    };
}

export function transformEffectiveAccess(raw) {
    const visibleGroups = pick(raw, "visible_groups", "visibleGroups") ?? [];
    const annotations = pick(raw, "visible_group_annotations", "visibleGroupAnnotations") ?? [];
    const roleSlugs = pick(raw, "role_slugs", "roleSlugs") ?? [];
    const configuredScope = pick(raw, "configured_scope", "configuredScope");
    return {
        bindingTable: pick(raw, "binding_table", "bindingTable") ?? "GRANT_BINDING_TABLE_UNSPECIFIED",
        grantee: grantee(raw?.grantee, pick(raw, "display_name", "displayName")),
        displayName: pick(raw, "display_name", "displayName") ?? "",
        roleSlugs: Array.isArray(roleSlugs) ? roleSlugs : [],
        visibleGroups: Array.isArray(visibleGroups) ? visibleGroups.map(groupKey).filter(Boolean) : [],
        visibleGroupAnnotations: Array.isArray(annotations) ? annotations.map(annotation) : [],
        effectiveSubjectCount: count(pick(raw, "effective_subject_count", "effectiveSubjectCount")),
        granteeMemberCount: count(pick(raw, "grantee_member_count", "granteeMemberCount")),
        granteePresence: enumTail(pick(raw, "grantee_presence", "granteePresence"), "GROUP_PRESENCE_"),
        unrestricted: Boolean(raw?.unrestricted),
        includeDeprovisioned: Boolean(pick(raw, "include_deprovisioned", "includeDeprovisioned")),
        impactDegraded: Boolean(pick(raw, "impact_degraded", "impactDegraded")),
        lastChangeAt: count(pick(raw, "last_change_at_unix_ms", "lastChangeAtUnixMs")),
        configuredScope: configuredScope ? {
            subjectCount: count(pick(configuredScope, "subject_count", "subjectCount")),
            unrestricted: Boolean(configuredScope?.unrestricted),
            impactDegraded: Boolean(pick(configuredScope, "impact_degraded", "impactDegraded")),
        } : null,
    };
}

export async function listEffectiveAccess({ pageSize = 25, pageToken = "", search = "", bindingTable = "GRANT_BINDING_TABLE_UNSPECIFIED" } = {}) {
    const raw = await restGet("/v1/access/effective", {
        page_size: pageSize,
        page_token: pageToken,
        search,
        binding_table: bindingTable,
    });
    return {
        entries: (raw?.entries ?? []).map(transformEffectiveAccess),
        nextPageToken: pick(raw, "next_page_token", "nextPageToken") ?? "",
        totalCount: count(pick(raw, "total_count", "totalCount")),
    };
}

export async function listGrantViewers({ pageSize = 25, pageToken = "", search = "" } = {}) {
    const raw = await restGet("/v1/access/viewers", { page_size: pageSize, page_token: pageToken, search });
    return {
        viewers: (raw?.viewers ?? []).map((viewer) => ({
            viewerUserId: pick(viewer, "viewer_user_id", "viewerUserId") ?? "",
            displayName: pick(viewer, "display_name", "displayName") ?? "",
            hasSubjectMapping: Boolean(pick(viewer, "has_subject_mapping", "hasSubjectMapping")),
        })),
        nextPageToken: pick(raw, "next_page_token", "nextPageToken") ?? "",
        totalCount: count(pick(raw, "total_count", "totalCount")),
    };
}

// Reverse visibility is intentionally content-free and therefore carries a
// viewer's opaque proto-bytes id, not an email/display label. The Access page is
// already grants-manage-gated and may use the separately gated candidate feed to
// resolve the small, displayed slice (at most ten nodes) without weakening that
// reverse-query contract. Keep the protobuf byte id in its gateway base64 form;
// the backend candidate search owns decoding at the storage boundary.
export async function resolveGrantViewerDisplay(viewerUserId) {
    if (!viewerUserId) return viewerUserId;
    const page = await listGrantViewers({ pageSize: 1, search: viewerUserId });
    return page.viewers.find((viewer) => viewer.viewerUserId === viewerUserId)?.displayName || viewerUserId;
}

export async function listGrantGroups({ pageSize = 25, pageToken = "", search = "", primaryDirectoryOnly = false } = {}) {
    const raw = await restGet("/v1/access/groups", {
        page_size: pageSize, page_token: pageToken, search,
        ...(primaryDirectoryOnly ? { primary_directory_only: true } : {}),
    });
    return {
        groups: (raw?.groups ?? []).map((option) => {
            const group = groupKey(option?.group) ?? { sourceProvider: "", integrationId: "", sourceId: "" };
            return {
                ...group,
                displayName: pick(option, "display_name", "displayName") || group.sourceId,
                memberCount: count(pick(option, "member_count", "memberCount")),
            };
        }),
        nextPageToken: pick(raw, "next_page_token", "nextPageToken") ?? "",
        totalCount: count(pick(raw, "total_count", "totalCount")),
        degraded: Boolean(raw?.degraded),
    };
}

export async function listGrantSubjects({ pageSize = 25, pageToken = "", search = "" } = {}) {
    const raw = await restGet("/v1/access/subjects", { page_size: pageSize, page_token: pageToken, search });
    return {
        subjects: (raw?.subjects ?? []).map((subject) => ({
            subjectIdentityId: pick(subject, "subject_identity_id", "subjectIdentityId") ?? "",
            displayName: pick(subject, "display_name", "displayName") ?? "",
            sourceProvider: pick(subject, "source_provider", "sourceProvider") ?? "",
            integrationId: pick(subject, "integration_id", "integrationId") ?? "",
            subjectSourceId: pick(subject, "subject_source_id", "subjectSourceId") ?? "",
            presence: enumTail(subject?.presence, "SUBJECT_PRESENCE_"),
        })),
        nextPageToken: pick(raw, "next_page_token", "nextPageToken") ?? "",
        totalCount: count(pick(raw, "total_count", "totalCount")),
        degraded: Boolean(raw?.degraded),
    };
}

function accessPerson(raw) {
    const subject = raw?.subject ?? {};
    return {
        subject: {
            subjectIdentityId: pick(subject, "subject_identity_id", "subjectIdentityId") ?? "",
            displayName: pick(subject, "display_name", "displayName") ?? "",
            sourceProvider: pick(subject, "source_provider", "sourceProvider") ?? "",
            integrationId: pick(subject, "integration_id", "integrationId") ?? "",
            subjectSourceId: pick(subject, "subject_source_id", "subjectSourceId") ?? "",
            presence: enumTail(subject?.presence, "SUBJECT_PRESENCE_"),
        },
        viewers: (raw?.viewers ?? []).map((viewer) => {
            const configuredAccess = pick(viewer, "configured_access", "configuredAccess");
            return {
                viewerUserId: pick(viewer, "viewer_user_id", "viewerUserId") ?? "",
                displayName: pick(viewer, "display_name", "displayName") ?? "",
                effectiveSubjectCount: count(pick(viewer, "effective_subject_count", "effectiveSubjectCount")),
                unrestricted: Boolean(viewer?.unrestricted),
                impactDegraded: Boolean(pick(viewer, "impact_degraded", "impactDegraded")),
                configuredAccess: configuredAccess ? transformEffectiveAccess(configuredAccess) : null,
            };
        }),
        viewerTotalCount: count(pick(raw, "viewer_total_count", "viewerTotalCount")),
    };
}

/**
 * Named people behind Effective Access counts. The server requires both
 * tenant.grants.read and tenant.directory_data.read; callers must also gate this
 * with hasSensitivePermission so permission loading never sends a broad request.
 */
export async function listAccessPeople({ pageSize = 25, pageToken = "", search = "", population = { type: "all" } } = {}) {
    let selector;
    if (population.type === "group") {
        selector = { directory_group: wireGroup(population.group) };
    } else if (population.type === "groupScope") {
        selector = { group_scope: { groups: (population.groups ?? []).map(wireGroup) } };
    } else if (population.type === "viewer") {
        selector = { viewer_user_id: population.viewerUserId };
    } else {
        selector = { all: {} };
    }
    const raw = await restPost("/v1/access/people:list", {
        page_size: pageSize,
        page_token: pageToken,
        search,
        ...selector,
    });
    const overCap = pick(raw, "over_cap", "overCap");
    return {
        people: (raw?.people ?? []).map(accessPerson),
        nextPageToken: pick(raw, "next_page_token", "nextPageToken") ?? "",
        totalCount: count(pick(raw, "total_count", "totalCount")),
        degraded: Boolean(raw?.degraded),
        generationId: count(pick(raw, "generation_id", "generationId")),
        overCap: overCap ? { count: count(overCap?.count), cap: count(overCap?.cap) } : null,
    };
}

export async function estimateGrantImpact({ grantee: value, scopeGroups }) {
    const raw = await restPost("/v1/access/grants:estimate", {
        grantee: wireGrantee(value),
        scope_groups: scopeGroups.map(wireGroup),
    });
    return {
        granteeCount: count(pick(raw, "grantee_count", "granteeCount")),
        subjectCount: count(pick(raw, "subject_count", "subjectCount")),
        coverage: (pick(raw, "scope_group_coverage", "scopeGroupCoverage") ?? []).map(annotation),
        granteeCoverage: annotation(pick(raw, "grantee_group_coverage", "granteeGroupCoverage")),
        degraded: Boolean(raw?.degraded),
    };
}

function transformHolder(raw) {
    return {
        bindingTable: pick(raw, "binding_table", "bindingTable") ?? "",
        grantee: grantee(raw?.grantee, pick(raw, "display_name", "displayName")),
        roleSlug: pick(raw, "role_slug", "roleSlug") ?? "",
        memberCount: count(pick(raw, "member_count", "memberCount")),
        presence: enumTail(raw?.presence, "GROUP_PRESENCE_"),
    };
}

export async function listAccessHolders(capability, { pageSize = 25, pageToken = "" } = {}) {
    const raw = await restGet("/v1/access/holders", {
        capability,
        page_size: pageSize,
        page_token: pageToken,
    });
    return {
        holders: (raw?.holders ?? []).map(transformHolder),
        nextPageToken: pick(raw, "next_page_token", "nextPageToken") ?? "",
        totalCount: count(pick(raw, "total_count", "totalCount")),
    };
}

export async function getAccessHealth({ pageSize = 50, pageToken = "" } = {}) {
    const raw = await restGet("/v1/access/health", {
        page_size: pageSize,
        page_token: pageToken,
    });
    const integrations = (raw?.integrations ?? []).map((integration) => ({
        integrationId: pick(integration, "integration_id", "integrationId") ?? "",
        displayName: pick(integration, "display_name", "displayName") ?? "",
        status: integration?.status ?? "",
        selectedGroupsOnly: Boolean(pick(integration, "selected_groups_only", "selectedGroupsOnly")),
        lastFullSyncAt: count(pick(integration, "last_full_sync_at_unix_ms", "lastFullSyncAtUnixMs")),
        estimatedNextFullSyncAt: count(pick(integration, "estimated_next_full_sync_at_unix_ms", "estimatedNextFullSyncAtUnixMs")),
    }));
    const rawIntegrationTotal = pick(raw, "integration_total_count", "integrationTotalCount");
    return {
        unresolvedActivityCount: count(pick(raw, "unresolved_activity_count", "unresolvedActivityCount")),
        observedActivityCount: count(pick(raw, "observed_activity_count", "observedActivityCount")),
        unresolvedActivityRatio: Number(pick(raw, "unresolved_activity_ratio", "unresolvedActivityRatio")) || 0,
        activeSubjectCount: count(pick(raw, "active_subject_count", "activeSubjectCount")),
        pin: raw?.pin ?? null,
        integrations,
        nextPageToken: pick(raw, "next_page_token", "nextPageToken") ?? "",
        integrationTotalCount: rawIntegrationTotal == null ? integrations.length : count(rawIntegrationTotal),
        degraded: Boolean(raw?.degraded),
    };
}

function visibilityNode(raw) {
    const grant = raw?.grant ?? {};
    return {
        bindingId: pick(grant, "binding_id", "bindingId") ?? "",
        grantee: grantee(grant?.grantee, pick(grant, "grantee_display", "granteeDisplay")),
        roleSlug: pick(grant, "role_slug", "roleSlug") ?? "",
        grantingGroupDisplay: pick(raw, "granting_group_display", "grantingGroupDisplay") || "",
        grantingGroupSourceId: pick(raw, "granting_group_source_id", "grantingGroupSourceId") ?? "",
        granteeGroupMemberCount: count(pick(raw, "grantee_group_member_count", "granteeGroupMemberCount")),
        granteePresence: enumTail(pick(raw, "grantee_presence", "granteePresence"), "GROUP_PRESENCE_"),
        conditionalOnViewDeprovisioned: Boolean(pick(raw, "conditional_on_view_deprovisioned", "conditionalOnViewDeprovisioned")),
    };
}

export async function describeSubjectVisibility(subjectIdentityId) {
    const raw = await restPost("/v1/access/subject-visibility:describe", {
        subject_identity_id: subjectIdentityId,
    });
    const nodeEnvelope = raw?.nodes;
    const overCap = pick(raw, "over_cap", "overCap");
    return {
        subjectIdentityId: pick(raw, "subject_identity_id", "subjectIdentityId") ?? subjectIdentityId,
        sourceProvider: pick(raw, "source_provider", "sourceProvider") ?? "",
        integrationId: pick(raw, "integration_id", "integrationId") ?? "",
        subjectSourceId: pick(raw, "subject_source_id", "subjectSourceId") ?? "",
        presence: enumTail(pick(raw, "subject_presence", "subjectPresence"), "SUBJECT_PRESENCE_"),
        unrestricted: {
            principalCount: count(pick(raw?.unrestricted, "principal_count", "principalCount")),
            groupBindingCount: count(pick(raw?.unrestricted, "group_binding_count", "groupBindingCount")),
        },
        nodes: (nodeEnvelope?.nodes ?? []).map(visibilityNode),
        overCap: overCap ? { count: count(overCap?.count), cap: count(overCap?.cap) } : null,
        pin: raw?.pin ?? null,
    };
}

export async function describeViewerScope(viewerUserId) {
    const raw = await restPost("/v1/access/viewer-scope:describe", {
        viewer_user_id: viewerUserId,
        form: "VIEWER_SCOPE_FORM_SUMMARY",
        required_permissions: ["tenant.events.read"],
    });
    const summary = raw?.summary ?? {};
    const grantedGroups = (pick(summary, "granted_groups", "grantedGroups") ?? []).map((entry) => ({
        group: groupKey(entry?.group),
        sourceId: pick(entry, "group_source_id", "groupSourceId") ?? "",
        memberCount: count(pick(entry, "member_count", "memberCount")),
        presence: enumTail(entry?.presence, "GROUP_PRESENCE_"),
    }));
    const rawGroupTotal = pick(summary, "granted_group_total_count", "grantedGroupTotalCount");
    return {
        viewerUserId: pick(summary, "viewer_user_id", "viewerUserId") ?? viewerUserId,
        disposition: enumTail(summary?.disposition, "VIEWER_SCOPE_DISPOSITION_"),
        effectiveSubjectCount: count(pick(summary, "effective_subject_count", "effectiveSubjectCount")),
        grantedGroups,
        grantedGroupTotalCount: rawGroupTotal == null ? grantedGroups.length : count(rawGroupTotal),
        includeDeprovisioned: Boolean(pick(summary, "include_deprovisioned", "includeDeprovisioned")),
        requiredPermissions: pick(summary, "required_permissions", "requiredPermissions") ?? [],
        deniedSurfaces: (pick(summary, "denied_surfaces", "deniedSurfaces") ?? []).map((surface) => ({
            rpcName: pick(surface, "rpc_name", "rpcName") ?? "",
            reason: enumTail(surface?.reason, "CLIENT_SCOPE_DENIAL_REASON_"),
        })),
        pin: summary?.pin ?? null,
    };
}

function actorDisplay(raw) {
    if (raw?.actorDisplay) return raw.actorDisplay;
    if (raw?.actor_display) return raw.actor_display;
    const actor = raw?.actor;
    return pick(actor, "operator_login", "operatorLogin")
        || pick(actor, "viewer_principal_id", "viewerPrincipalId")
        || (actor?.system ? "System" : "Unknown actor");
}

export async function listAccessChanges(bindingId, { pageSize = 20, pageToken = "" } = {}) {
    const raw = await restGet("/v1/access/changes", {
        binding_id: bindingId,
        page_size: pageSize,
        page_token: pageToken,
    });
    return {
        changes: (raw?.changes ?? []).map((change) => ({
            changeId: pick(change, "change_id", "changeId") ?? "",
            occurredAt: count(pick(change, "occurred_at_unix_ms", "occurredAtUnixMs")),
            actorDisplay: actorDisplay(change),
            action: enumTail(change?.action, "GRANT_ACTION_"),
            roleSlug: pick(change, "role_slug", "roleSlug") ?? "",
        })),
        nextPageToken: pick(raw, "next_page_token", "nextPageToken") ?? "",
        totalCount: count(pick(raw, "total_count", "totalCount")),
        totalCountCapped: Boolean(pick(raw, "total_count_capped", "totalCountCapped")),
    };
}

export async function listAccessDenials(viewerUserId, { pageSize = 20, pageToken = "" } = {}) {
    const raw = await restGet("/v1/access/denials", {
        viewer_principal_id: viewerUserId,
        page_size: pageSize,
        page_token: pageToken,
    });
    return {
        denials: (raw?.denials ?? []).map((denial) => ({
            occurredAt: count(pick(denial, "occurred_at_unix_ms", "occurredAtUnixMs")),
            rpcName: pick(denial, "rpc_name", "rpcName") ?? "",
            reason: enumTail(denial?.reason, "DENIAL_REASON_"),
            coalescedCount: count(pick(denial, "coalesced_count", "coalescedCount")) || 1,
        })),
        nextPageToken: pick(raw, "next_page_token", "nextPageToken") ?? "",
        totalCount: count(pick(raw, "total_count", "totalCount")),
        totalCountCapped: Boolean(pick(raw, "total_count_capped", "totalCountCapped")),
    };
}
